import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
  IconCTAButton,
} from "./utils";
import { capitalizeEachWord } from "../utils/string";

interface AdvancedAgGridSearchProps {
  tableIdentifier: string;
  gridApi: any;
  onApplyFilter?: (filters: FilterRule[]) => void;
  buttonLabel?: string;
  onClose?: () => void;
  onClear?: () => void;
}

interface FilterRule {
  id: string;
  column: string;
  operator:
    | "contains"
    | "equals"
    | "startsWith"
    | "endsWith"
    | "notContains"
    | "notEqual"
    | "lessThan"
    | "greaterThan"
    | "inRange";
  value: string;
  delimiter: "comma" | "space" | "newline" | "auto";
  condition: "AND" | "OR";
  selectedInnerFilter?: string; // Title of the selected inner filter (e.g., "Employee Name")
}

type FilterKind = "text" | "set" | "number" | "date" | "multi";

interface InnerFilterMetadata {
  title: string;
  filterType: string; // e.g., "agSetColumnFilter", "agTextColumnFilter"
  kind: FilterKind;
}

interface ColumnOption {
  field: string;
  headerName: string;
  colId: string;
  kind: FilterKind;
  innerFilters: string[]; // Legacy: array of filter type strings
  innerFiltersMetadata: InnerFilterMetadata[]; // New: detailed metadata
  def: any;
}

export default function AdvancedAgGridSearch({
  tableIdentifier,
  gridApi,
  onApplyFilter,
  buttonLabel = "Advanced Filter(s)",
}: AdvancedAgGridSearchProps) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([]);
  const [columnByField, setColumnByField] = useState<
    Record<string, ColumnOption>
  >({});
  const [filterDescription, setFilterDescription] = useState<string>("");
  const [triggerUpdate, setTriggerUpdate] = useState<number>(0);

  const operatorOptionsForKind = (kind: FilterKind) => {
    if (kind === "text")
      return [
        "contains",
        "equals",
        "startsWith",
        "endsWith",
        "notContains",
      ] as const;
    if (kind === "number")
      return ["equals", "notEqual", "lessThan", "greaterThan"] as const;
    if (kind === "date") return ["equals", "notEqual"] as const;
    return ["equals"] as const; // set/multi
  };
  const defaultOperatorForKind = (kind: FilterKind) =>
    kind === "text" ? "contains" : "equals";
  const inferKind = (colDef: any): FilterKind => {
    const f = colDef?.filter;
    if (f === "agSetColumnFilter") return "set";
    if (f === "agNumberColumnFilter") return "number";
    if (f === "agDateColumnFilter") return "date";
    if (f === "agMultiColumnFilter") return "multi";
    return "text";
  };
  const inferKindFromFilterType = (filterType: string): FilterKind => {
    if (filterType === "agSetColumnFilter") return "set";
    if (filterType === "agNumberColumnFilter") return "number";
    if (filterType === "agDateColumnFilter") return "date";
    if (filterType === "agMultiColumnFilter") return "multi";
    return "text";
  };
  const getInnerFilters = (colDef: any): string[] => {
    const arr = colDef?.filterParams?.filters || [];
    return arr
      .map((x: any) => (typeof x === "string" ? x : x?.filter || ""))
      .filter(Boolean);
  };
  const getInnerFiltersMetadata = (colDef: any): InnerFilterMetadata[] => {
    const arr = colDef?.filterParams?.filters || [];
    return arr
      .map((x: any) => {
        if (typeof x === "string") {
          return {
            title: x,
            filterType: x,
            kind: inferKindFromFilterType(x),
          };
        }
        const filterType = x?.filter || "";
        if (!filterType) return null;
        return {
          title: x?.title || filterType,
          filterType,
          kind: inferKindFromFilterType(filterType),
        };
      })
      .filter(Boolean) as InnerFilterMetadata[];
  };
  const kindLabel = (kind: FilterKind, innerFilters: string[]) => {
    if (kind !== "multi") return kind;
    if (!innerFilters.length) return "multi";
    const short = innerFilters
      .map((t) =>
        t === "agTextColumnFilter"
          ? "text"
          : t === "agSetColumnFilter"
          ? "set"
          : t === "agNumberColumnFilter"
          ? "number"
          : t === "agDateColumnFilter"
          ? "date"
          : "?"
      )
      .join("+");
    return `multi(${short})`;
  };

  const parseFilterModel = (filterModel: any) => {
    try {
      if (!filterModel || typeof filterModel !== "object")
        return "No filters applied.";
      const cols = Object.keys(filterModel);
      if (!cols.length) return "No filters applied.";
      const parts: string[] = [];
      cols.forEach((col) => {
        const model = filterModel[col];
        const humanizedCol = col.replace(/([A-Z])/g, " $1").toUpperCase();
        const colMeta =
          columnOptions.find((c) => c.colId === col) ||
          columnOptions.find((c) => c.field === col) ||
          null;
        if (!model || typeof model !== "object") return;
        if (model.filterType === "multi") {
          const inners: any[] = Array.isArray(model.filterModels)
            ? model.filterModels
            : [];

          const availableDefs: any[] =
            colMeta?.def?.filterParams?.filters || [];

          const innerParts: string[] = [];

          if (availableDefs && availableDefs.length) {
            availableDefs.forEach((filterDef: any, idx: number) => {
              const title =
                typeof filterDef === "string"
                  ? filterDef
                  : filterDef?.title || "";
              const m = inners[idx];
              if (
                !m ||
                typeof m !== "object" ||
                (!m.filterType &&
                  m.dateFrom === undefined &&
                  m.dateTo === undefined)
              )
                return;

              let desc = "";
              if (m.filterType === "set") {
                const vals = Array.isArray(m.values) ? m.values.join(", ") : "";
                desc = vals ? `set: ${vals}` : "";
              } else if (m.filterType === "text") {
                const conds = Array.isArray(m.conditions) ? m.conditions : [];
                if (conds.length) {
                  const op = m.operator || "OR";
                  const txt = conds
                    .filter((c: any) => c && typeof c === "object")
                    .map((c: any) => `${c.type} "${c.filter ?? ""}"`)
                    .join(` ${op} `);
                  desc = `text: (${txt})`;
                } else {
                  desc = m.filter ? `text: ${m.type} "${m.filter}"` : "";
                }
              } else if (m.filterType === "number") {
                const conds = Array.isArray(m.conditions) ? m.conditions : [];
                if (conds.length) {
                  const op = m.operator || "OR";
                  const num = conds
                    .filter((c: any) => c && typeof c === "object")
                    .map((c: any) => `${c.type} ${c.filter ?? ""}`)
                    .join(` ${op} `);
                  desc = `number: (${num})`;
                } else {
                  desc =
                    (m.filter ?? "") !== ""
                      ? `number: ${m.type} ${m.filter}`
                      : "";
                }
              } else if (
                m.filterType === "date" ||
                m.dateFrom !== undefined ||
                m.dateTo !== undefined
              ) {
                const conds = Array.isArray(m.conditions) ? m.conditions : [];
                if (conds.length) {
                  const op = m.operator || "OR";
                  const parts = conds
                    .filter((c: any) => c && typeof c === "object")
                    .map((c: any) => {
                      if (
                        c &&
                        typeof c === "object" &&
                        (c.dateFrom || c.dateTo || c.filter !== undefined)
                      ) {
                        const rawFrom = c.dateFrom ?? c.filter ?? "";
                        const rawTo = c.dateTo ?? "";
                        const from = formatDateForDisplay(rawFrom);
                        const to = rawTo && formatDateForDisplay(rawTo);
                        if (from && to) return `${from} to ${to}`;
                        return from || "";
                      }
                      return "";
                    })
                    .filter(Boolean);
                  if (parts.length)
                    desc =
                      parts.length > 1
                        ? `(${parts.join(` ${op} `)})`
                        : parts[0];
                } else {
                  const rawFrom = m.dateFrom ?? m.filter ?? "";
                  const rawTo = m.dateTo ?? "";
                  const from = formatDateForDisplay(rawFrom);
                  const to = rawTo && formatDateForDisplay(rawTo);
                  if (from && to) desc = `${from} to ${to}`;
                  else desc = from || "";
                }
              }

              if (desc) innerParts.push(title ? `${title}: ${desc}` : desc);
            });
          } else {
            inners
              .filter(
                (m) =>
                  m &&
                  typeof m === "object" &&
                  (m.filterType ||
                    m.dateFrom !== undefined ||
                    m.dateTo !== undefined)
              )
              .map((m) => {
                if (m.filterType === "set") {
                  const vals = Array.isArray(m.values)
                    ? m.values.join(", ")
                    : "";
                  return vals ? `set: ${vals}` : "";
                }
                if (m.filterType === "text") {
                  const conds = Array.isArray(m.conditions) ? m.conditions : [];
                  if (conds.length) {
                    const op = m.operator || "OR";
                    const txt = conds
                      .filter((c: any) => c && typeof c === "object")
                      .map((c: any) => `${c.type} "${c.filter ?? ""}"`)
                      .join(` ${op} `);
                    return `text: (${txt})`;
                  }
                  return m.filter ? `text: ${m.type} "${m.filter}"` : "";
                }
                if (m.filterType === "number") {
                  const conds = Array.isArray(m.conditions) ? m.conditions : [];
                  if (conds.length) {
                    const op = m.operator || "OR";
                    const num = conds
                      .filter((c: any) => c && typeof c === "object")
                      .map((c: any) => `${c.type} ${c.filter ?? ""}`)
                      .join(` ${op} `);
                    return `number: (${num})`;
                  }
                  return (m.filter ?? "") !== ""
                    ? `number: ${m.type} ${m.filter}`
                    : "";
                }
                if (m.filterType === "date") {
                  const rawFrom = m.dateFrom ?? m.filter ?? "";
                  const rawTo = m.dateTo ?? "";
                  const from = formatDateForDisplay(rawFrom);
                  const to = rawTo && formatDateForDisplay(rawTo);
                  if (from && to) return `${from} to ${to}`;
                  return from || "";
                }
                return "";
              })
              .filter(Boolean)
              .forEach((s) => innerParts.push(s));
          }

          if (innerParts.length)
            parts.push(`${humanizedCol}: ${innerParts.join("; ")}`);
          return;
        }
        if (model.filterType === "set") {
          const vals = Array.isArray(model.values)
            ? model.values.join(", ")
            : "";
          if (vals) parts.push(`${humanizedCol}: ${vals}`);
          return;
        }
        if (model.filterType === "text") {
          const conds = Array.isArray(model.conditions) ? model.conditions : [];
          if (conds.length) {
            const op = model.operator || "OR";
            const txt = conds
              .filter((c: any) => c && typeof c === "object")
              .map((c: any) => `${c.type} "${c.filter ?? ""}"`)
              .join(` ${op} `);
            parts.push(`${humanizedCol} (${txt})`);
          } else if (model.filter) {
            parts.push(`${humanizedCol} ${model.type} "${model.filter}"`);
          }
          return;
        }
        if (model.filterType === "number") {
          const conds = Array.isArray(model.conditions) ? model.conditions : [];
          if (conds.length) {
            const op = model.operator || "OR";
            const num = conds
              .filter((c: any) => c && typeof c === "object")
              .map((c: any) => `${c.type} ${c.filter ?? ""}`)
              .join(` ${op} `);
            parts.push(`${humanizedCol} (${num})`);
          } else if ((model.filter ?? "") !== "") {
            parts.push(`${humanizedCol} ${model.type} ${model.filter}`);
          }
          return;
        }
        if (
          model.filterType === "date" ||
          model.dateFrom !== undefined ||
          model.dateTo !== undefined
        ) {
          const rawFrom = model.dateFrom ?? model.filter ?? "";
          const rawTo = model.dateTo ?? "";
          const from = formatDateForDisplay(rawFrom);
          const to = rawTo && formatDateForDisplay(rawTo);
          if (from)
            parts.push(`${humanizedCol}: ${from}${to ? ` to ${to}` : ""}`);
        }
      });
      return parts.length ? `${parts.join("\n")}` : "No filters applied.";
    } catch {
      return "No filters applied.";
    }
  };

  const countActiveFilters = (filterModel: any): number => {
    if (!filterModel || typeof filterModel !== "object") return 0;
    let count = 0;
    Object.values(filterModel).forEach((m: any) => {
      if (!m || typeof m !== "object") return;
      if (m.filterType === "multi") {
        const inners: any[] = Array.isArray(m.filterModels)
          ? m.filterModels
          : [];
        inners.forEach((im) => {
          if (!im || typeof im !== "object") return;
          if (
            im.filterType === "set" &&
            Array.isArray(im.values) &&
            im.values.length
          )
            count += 1;
          else if (im.filterType === "text") {
            const conds = Array.isArray(im.conditions) ? im.conditions : [];
            if (
              (conds.length && conds.some((c: any) => c?.filter)) ||
              im.filter
            )
              count += 1;
          } else if (im.filterType === "number") {
            const conds = Array.isArray(im.conditions) ? im.conditions : [];
            if (
              (conds.length &&
                conds.some((c: any) => c?.filter !== undefined)) ||
              im.filter !== undefined
            )
              count += 1;
          } else if (
            im.filterType === "date" ||
            im.dateFrom !== undefined ||
            im.dateTo !== undefined
          ) {
            const conds = Array.isArray(im.conditions) ? im.conditions : [];
            const condHasDate =
              conds.length &&
              conds.some(
                (c: any) =>
                  !!(
                    c &&
                    (c.filter !== undefined ||
                      c.dateFrom !== undefined ||
                      c.dateTo !== undefined)
                  )
              );
            const hasAnyDate =
              !!im.dateFrom ||
              !!im.dateTo ||
              !!condHasDate ||
              im.filter !== undefined;
            if (hasAnyDate) count += 1;
          }
        });
        return;
      }
      if (m.filterType === "set" && Array.isArray(m.values) && m.values.length)
        count += 1;
      else if (m.filterType === "text") {
        const conds = Array.isArray(m.conditions) ? m.conditions : [];
        if ((conds.length && conds.some((c: any) => c?.filter)) || m.filter)
          count += 1;
      } else if (m.filterType === "number") {
        const conds = Array.isArray(m.conditions) ? m.conditions : [];
        if (
          (conds.length && conds.some((c: any) => c?.filter !== undefined)) ||
          m.filter !== undefined
        )
          count += 1;
      } else if (
        m.filterType === "date" ||
        m.dateFrom !== undefined ||
        m.dateTo !== undefined
      ) {
        const conds = Array.isArray(m.conditions) ? m.conditions : [];
        const condHasDate =
          conds.length &&
          conds.some(
            (c: any) =>
              !!(
                c &&
                (c.filter !== undefined ||
                  c.dateFrom !== undefined ||
                  c.dateTo !== undefined)
              )
          );
        const hasAnyDate =
          !!m.dateFrom || !!m.dateTo || !!condHasDate || m.filter !== undefined;
        if (hasAnyDate) count += 1;
      }
    });
    return count;
  };

  useEffect(() => {
    if (!gridApi || !open) return;
    const defs: any[] = Array.isArray(gridApi.getColumnDefs?.())
      ? (gridApi.getColumnDefs() as any[])
      : [];
    const usable = (defs || []).filter((d) => d?.field && d.filter !== false);
    const options: ColumnOption[] = usable.map((d) => {
      const kind = inferKind(d);
      return {
        field: d.field,
        headerName: d.headerName || d.field,
        colId: d.colId || d.field,
        kind,
        innerFilters: getInnerFilters(d),
        innerFiltersMetadata: getInnerFiltersMetadata(d),
        def: d,
      };
    });
    // visible first
    options.sort((a, b) => {
      const da = defs.find((d) => d.field === a.field);
      const db = defs.find((d) => d.field === b.field);
      const ha = da?.hide ? 1 : 0;
      const hb = db?.hide ? 1 : 0;
      return ha - hb;
    });
    setColumnOptions(options);
    setColumnByField(Object.fromEntries(options.map((o) => [o.field, o])));

    const currentModel = gridApi.getFilterModel?.() || {};
    setFilterDescription(parseFilterModel(currentModel));
  }, [gridApi, open]);

  useEffect(() => {
    if (!open) return;
    if (columnOptions.length && filters.length === 0) {
      const firstCol = columnOptions[0];
      const hasInnerFilters = firstCol.innerFiltersMetadata.length > 0;
      const firstInnerFilter = hasInnerFilters
        ? firstCol.innerFiltersMetadata[0].title
        : undefined;
      const effectiveKind = hasInnerFilters
        ? firstCol.innerFiltersMetadata[0].kind
        : firstCol.kind;

      setFilters([
        {
          id: `${Date.now()}`,
          column: firstCol.field,
          operator: defaultOperatorForKind(
            effectiveKind
          ) as FilterRule["operator"],
          value: "",
          delimiter: "auto",
          condition: "AND",
          selectedInnerFilter: firstInnerFilter,
        },
      ]);
    }
  }, [open, columnOptions, filters.length]);

  const parseValues = (value: string, delimiter: string): string[] => {
    if (!value) return [];
    if (delimiter === "auto") {
      if (value.includes("\n"))
        return value
          .split("\n")
          .map((v) => v)
          .filter(Boolean);
      if (value.includes(","))
        return value
          .split(",")
          .map((v) => v)
          .filter(Boolean);
      if (/\s/.test(value))
        return value
          .split(/\s+/)
          .map((v) => v)
          .filter(Boolean);
      return [value].filter(Boolean);
    }
    const splitChar =
      delimiter === "comma" ? "," : delimiter === "space" ? " " : "\n";
    return value
      .split(splitChar)
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const mapNumberOp = (op: FilterRule["operator"]) => {
    if (op === "notContains") return "notEqual";
    if (op === "contains" || op === "startsWith" || op === "endsWith")
      return "equals";
    return op;
  };

  const mapDateOp = (valsLen: number, op: FilterRule["operator"]) => {
    if (op === "inRange" && valsLen >= 2) return "inRange";
    if (op === "notEqual") return "notEqual";
    if (op === "equals") return "equals";
    return "equals";
  };

  const normalizeDateString = (input: string): string => {
    if (!input) return "";
    const s = String(input).trim();
    if (!s) return "";

    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }

    const m1 = s.match(/^(\d{1,2})(?:\/|-)(\d{1,2})(?:\/|-)(\d{4})$/);
    if (m1) {
      const day = Number(m1[1]);
      const month = Number(m1[2]);
      const year = Number(m1[3]);
      const dt = new Date(year, month - 1, day);
      if (!Number.isNaN(dt.getTime())) {
        const y = dt.getFullYear();
        const mo = String(dt.getMonth() + 1).padStart(2, "0");
        const dd = String(dt.getDate()).padStart(2, "0");
        return `${y}-${mo}-${dd}`;
      }
    }

    const m2 = s.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
    if (m2) {
      const day = Number(m2[1]);
      const monthName = m2[2].toLowerCase();
      const year = Number(m2[3]);
      const months: Record<string, number> = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
      };
      const mon = months[monthName.substring(0, 3)];
      if (mon !== undefined) {
        const dt = new Date(year, mon, day);
        if (!Number.isNaN(dt.getTime())) {
          const y = dt.getFullYear();
          const mo = String(dt.getMonth() + 1).padStart(2, "0");
          const dd = String(dt.getDate()).padStart(2, "0");
          return `${y}-${mo}-${dd}`;
        }
      }
    }

    return "";
  };

  const formatDateForDisplay = (input: string): string => {
    if (!input) return "";
    const s = String(input).trim();
    if (!s) return "";
    const norm = normalizeDateString(s);
    return norm || s;
  };

  const buildTextModel = (rules: FilterRule[]) => {
    const conditions: any[] = [];
    let op: "AND" | "OR" = "OR";
    rules.forEach((r, idx) => {
      if (idx === 0) op = r.condition;
      const type = r.operator;
      parseValues(r.value, r.delimiter).forEach((v) => {
        conditions.push({ filterType: "text", type, filter: v });
      });
    });
    if (conditions.length <= 1) {
      const c = conditions[0] || { type: "contains", filter: "" };
      return { filterType: "text", type: c.type, filter: c.filter };
    }
    return { filterType: "text", operator: op, conditions };
  };

  const buildNumberModel = (rules: FilterRule[]) => {
    const conditions: any[] = [];
    rules.forEach((r) => {
      const type = mapNumberOp(r.operator);
      parseValues(r.value, r.delimiter).forEach((v) => {
        const n = Number(v);
        if (!Number.isNaN(n))
          conditions.push({ filterType: "number", type, filter: n });
      });
    });

    if (conditions.length <= 1) {
      const c = conditions[0] || { type: "equals", filter: 0 };
      return { filterType: "number", type: c.type, filter: c.filter };
    }

    let operator: "AND" | "OR" = "OR";
    if (rules.length === 1) {
      operator = "OR";
    } else if (rules.length > 1) {
      operator = rules[0]?.condition === "AND" ? "AND" : "OR";
    }

    return { filterType: "number", operator, conditions };
  };

  const buildSetModel = (rules: FilterRule[]) => {
    const allVals = new Set<string>();
    rules.forEach((r) => {
      parseValues(r.value, r.delimiter).forEach((v) => allVals.add(v));
    });
    return { filterType: "set", values: Array.from(allVals) };
  };

  const buildDateModel = (rules: FilterRule[]) => {
    const r = rules[0];
    const rawVals = parseValues(r.value, r.delimiter);
    const vals = rawVals.map((v) => normalizeDateString(v)).filter(Boolean);
    const t = mapDateOp(vals.length, r.operator);

    if (r.operator === "inRange" && vals.length >= 2) {
      return {
        filterType: "date",
        type: "inRange",
        dateFrom: vals[0],
        dateTo: vals[1],
      };
    }

    if (vals.length >= 2) {
      const conditions: any[] = [];
      vals.forEach((v) => {
        conditions.push({ filterType: "date", type: t, dateFrom: v });
      });
      return { filterType: "date", operator: "OR", conditions };
    }

    return { filterType: "date", type: t, dateFrom: vals[0] || "" };
  };

  const applyFiltersToGrid = (filterRules: FilterRule[]) => {
    if (!gridApi) return;
    const defs: any[] = Array.isArray(gridApi.getColumnDefs?.())
      ? (gridApi.getColumnDefs() as any[])
      : [];
    if (!defs.length) return;
    const firstField =
      columnOptions[0]?.field || defs.find((d) => d?.field)?.field || "";
    const normalized = (filterRules || [])
      .map((r) => (!r.column ? { ...r, column: firstField } : r))
      .filter(
        (r) =>
          r.column &&
          String(r.value || "").trim().length > 0 &&
          !!columnByField[r.column]
      );

    const grouped: Record<
      string,
      { kind: FilterKind; def: any; rules: FilterRule[] }
    > = {};
    normalized.forEach((r) => {
      const meta = columnByField[r.column];
      if (!meta) return;
      if (!grouped[meta.colId])
        grouped[meta.colId] = { kind: meta.kind, def: meta.def, rules: [] };
      grouped[meta.colId].rules.push(r);
    });

    const filterModel: any = {};
    Object.entries(grouped).forEach(([colKey, group]) => {
      const { kind, rules, def } = group;

      if (kind === "multi") {
        // Group rules by selected inner filter
        const rulesByInnerFilter: Record<string, FilterRule[]> = {};
        rules.forEach((rule) => {
          const innerTitle = rule.selectedInnerFilter || "";
          if (!rulesByInnerFilter[innerTitle]) {
            rulesByInnerFilter[innerTitle] = [];
          }
          rulesByInnerFilter[innerTitle].push(rule);
        });

        const meta = columnByField[colKey];
        const available = def?.filterParams?.filters || [];

        const innerModels: any[] = [];

        available.forEach((filterDef: any) => {
          const filterTitle =
            typeof filterDef === "string" ? filterDef : filterDef?.title;
          if (!filterTitle) return;

          const innerRules = rulesByInnerFilter[filterTitle];
          if (!innerRules || innerRules.length === 0) {
            innerModels.push(null);
            return;
          }

          const innerMeta = meta?.innerFiltersMetadata?.find(
            (im) => im.title === filterTitle
          );
          if (!innerMeta) {
            innerModels.push(null);
            return;
          }

          let model: any = null;
          if (innerMeta.kind === "text") model = buildTextModel(innerRules);
          else if (innerMeta.kind === "set") model = buildSetModel(innerRules);
          else if (innerMeta.kind === "number")
            model = buildNumberModel(innerRules);
          else if (innerMeta.kind === "date")
            model = buildDateModel(innerRules);

          innerModels.push(model);
        });

        if (innerModels.length > 0) {
          filterModel[colKey] = {
            filterType: "multi",
            filterModels: innerModels,
          };
        }
        return;
      }

      if (kind === "set") {
        const m = buildSetModel(rules);
        if ((m.values || []).length > 0) filterModel[colKey] = m;
        return;
      }

      if (kind === "text") {
        const m = buildTextModel(rules);
        if (
          (m.conditions && m.conditions.length > 0) ||
          (typeof m.filter === "string" && m.filter.length > 0)
        ) {
          filterModel[colKey] = m;
        }
        return;
      }

      if (kind === "number") {
        const m = buildNumberModel(rules);
        if (
          (m.conditions && m.conditions.length > 0) ||
          (typeof m.filter === "number" && !Number.isNaN(m.filter))
        ) {
          filterModel[colKey] = m;
        }
        return;
      }

      if (kind === "date") {
        const m = buildDateModel(rules);
        if (m.dateFrom) filterModel[colKey] = m;
      }
    });

    gridApi.setFilterModel(filterModel);
    gridApi.onFilterChanged?.();
    setFilterDescription(parseFilterModel(filterModel));
  };

  const addFilter = () => {
    if (!columnOptions.length) return;
    const first = columnOptions[0];
    const hasInnerFilters = first.innerFiltersMetadata.length > 0;
    const firstInnerFilter = hasInnerFilters
      ? first.innerFiltersMetadata[0].title
      : undefined;
    const effectiveKind = hasInnerFilters
      ? first.innerFiltersMetadata[0].kind
      : first.kind;

    setFilters((prev) => [
      ...prev,
      {
        id: `${Date.now()}`,
        column: first.field,
        operator: defaultOperatorForKind(
          effectiveKind
        ) as FilterRule["operator"],
        value: "",
        delimiter: "auto",
        condition: "AND",
        selectedInnerFilter: firstInnerFilter,
      },
    ]);
  };

  const removeFilter = (id: string) => {
    setFilters((prev) => {
      const next = prev.filter((f) => f.id !== id);
      if (next.length === 0 && columnOptions.length) {
        const first = columnOptions[0];
        const hasInnerFilters = first.innerFiltersMetadata.length > 0;
        const firstInnerFilter = hasInnerFilters
          ? first.innerFiltersMetadata[0].title
          : undefined;
        const effectiveKind = hasInnerFilters
          ? first.innerFiltersMetadata[0].kind
          : first.kind;

        return [
          {
            id: `${Date.now()}`,
            column: first.field,
            operator: defaultOperatorForKind(
              effectiveKind
            ) as FilterRule["operator"],
            value: "",
            delimiter: "auto",
            condition: "AND",
            selectedInnerFilter: firstInnerFilter,
          },
        ];
      }
      return next;
    });
  };

  const updateFilter = (id: string, field: keyof FilterRule, value: any) => {
    setFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [field]: value } : f))
    );
  };

  const handleOpen = () => {
    const currentModel = gridApi?.getFilterModel?.() || {};
    setFilterDescription(parseFilterModel(currentModel));
    setOpen(true);
  };

  const handleApply = () => {
    applyFiltersToGrid(filters);
    onApplyFilter?.(filters);
    setOpen(false);
  };

  const handleClear = () => {
    if (columnOptions.length) {
      const first = columnOptions[0];
      const hasInnerFilters = first.innerFiltersMetadata.length > 0;
      const firstInnerFilter = hasInnerFilters
        ? first.innerFiltersMetadata[0].title
        : undefined;
      const effectiveKind = hasInnerFilters
        ? first.innerFiltersMetadata[0].kind
        : first.kind;

      setFilters([
        {
          id: `${Date.now()}`,
          column: first.field,
          operator: defaultOperatorForKind(
            effectiveKind
          ) as FilterRule["operator"],
          value: "",
          delimiter: "auto",
          condition: "AND",
          selectedInnerFilter: firstInnerFilter,
        },
      ]);
    } else {
      setFilters([]);
    }
    gridApi?.setFilterModel(null);
    gridApi?.onFilterChanged?.();
    const currentModel = gridApi?.getFilterModel?.() || {};
    setFilterDescription(parseFilterModel(currentModel));
  };

  useEffect(() => {
    if (!gridApi) return undefined;

    const onFilterChanged = () => {
      const currentModel = gridApi.getFilterModel?.() || {};
      setFilterDescription(parseFilterModel(currentModel));
      setTriggerUpdate((prev) => prev + 1);

      try {
        const hasFilters = currentModel && Object.keys(currentModel).length > 0;
        if (!hasFilters) {
          if (columnOptions.length) {
            const first = columnOptions[0];
            const hasInnerFilters = first.innerFiltersMetadata.length > 0;
            const firstInnerFilter = hasInnerFilters
              ? first.innerFiltersMetadata[0].title
              : undefined;
            const effectiveKind = hasInnerFilters
              ? first.innerFiltersMetadata[0].kind
              : first.kind;

            setFilters([
              {
                id: `${Date.now()}`,
                column: first.field,
                operator: defaultOperatorForKind(
                  effectiveKind
                ) as FilterRule["operator"],
                value: "",
                delimiter: "auto",
                condition: "AND",
                selectedInnerFilter: firstInnerFilter,
              },
            ]);
          } else {
            setFilters([]);
          }
        }
      } catch (e) {
        // swallow any errors here to avoid breaking grid event flow
      }
    };

    const unsubscribe = gridApi?.addEventListener?.(
      "filterChanged",
      onFilterChanged
    );

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [gridApi]);

  const activeCount = useMemo(() => {
    try {
      const model = gridApi?.getFilterModel?.() || {};
      return countActiveFilters(model);
    } catch {
      return 0;
    }
  }, [gridApi, filterDescription, triggerUpdate]);

  return (
    <>
      <div className="inline-flex items-center">
        <IconCTAButton
          onClick={handleOpen}
          iconName={"mdi:filter"}
          className={
            "px-4 font-medium items-center flex flex-row text-gray-600"
          }
        >
          <span>{buttonLabel}</span>
          {activeCount > 0 && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-orange-501 text-white">
              {activeCount}
            </span>
          )}
        </IconCTAButton>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <HStack className="justify-between items-center">
            <span className="text-lg font-semibold">Advanced Filter</span>
            <Icon
              icon="mdi:close"
              className="cursor-pointer text-gray-500 hover:text-gray-700"
              width={24}
              onClick={() => setOpen(false)}
            />
          </HStack>
        </DialogTitle>
        <DialogContent>
          <VStack className="gap-4 py-4">
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <HStack className="gap-2 items-start">
                <Icon
                  icon="material-symbols:info-outline"
                  className="text-blue-600 mt-0.5"
                  width={20}
                />
                <VStack className="text-xs text-blue-700">
                  <p className="font-medium">Current Filters:</p>
                  <p className="whitespace-pre-line">{filterDescription}</p>
                </VStack>
              </HStack>
            </div>
            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
              <HStack className="gap-2 items-start">
                <Icon
                  icon="material-symbols:info-outline"
                  className="text-blue-600 mt-0.5"
                  width={20}
                />
                <VStack className="text-xs text-blue-700">
                  <p className="font-medium">Paste from Excel:</p>
                  <p>
                    You can paste values directly from Excel. The delimiter will
                    be auto-detected.
                  </p>
                </VStack>
              </HStack>
            </div>

            {filters.map((filter, index) => {
              const columnMeta = columnByField[filter.column];
              const hasInnerFilters =
                columnMeta?.innerFiltersMetadata?.length > 0;

              // Determine the effective filter kind
              let currentKind: FilterKind = columnMeta?.kind || "text";
              if (hasInnerFilters && filter.selectedInnerFilter) {
                const selectedInner = columnMeta.innerFiltersMetadata.find(
                  (inner) => inner.title === filter.selectedInnerFilter
                );
                if (selectedInner) {
                  currentKind = selectedInner.kind;
                }
              }

              const ops = operatorOptionsForKind(
                currentKind
              ) as readonly string[];
              const labelKind = kindLabel(
                columnMeta?.kind || "text",
                columnMeta?.innerFilters || []
              );

              return (
                <VStack
                  key={filter.id}
                  className="gap-3 p-4 border rounded-lg bg-gray-50"
                >
                  {index > 0 && (
                    <HStack className="gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Condition:
                      </label>
                      <select
                        value={filter.condition}
                        onChange={(e) =>
                          updateFilter(
                            filter.id,
                            "condition",
                            e.target.value as "AND" | "OR"
                          )
                        }
                        className="px-3 py-1 border rounded-md text-sm bg-white"
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    </HStack>
                  )}

                  <HStack className="gap-3 items-end">
                    <VStack className="flex-1">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Column
                      </label>
                      <select
                        value={filter.column}
                        onChange={(e) => {
                          const nextField = e.target.value;
                          const meta = columnByField[nextField];
                          const hasInner =
                            meta?.innerFiltersMetadata?.length > 0;
                          const firstInner = hasInner
                            ? meta.innerFiltersMetadata[0].title
                            : undefined;
                          const effectiveKind = hasInner
                            ? meta.innerFiltersMetadata[0].kind
                            : meta?.kind || "text";
                          const allowed = operatorOptionsForKind(
                            effectiveKind
                          ) as readonly string[];
                          const nextOperator = allowed.includes(filter.operator)
                            ? filter.operator
                            : (defaultOperatorForKind(
                                effectiveKind
                              ) as FilterRule["operator"]);
                          updateFilter(filter.id, "column", nextField);
                          updateFilter(filter.id, "operator", nextOperator);
                          updateFilter(
                            filter.id,
                            "selectedInnerFilter",
                            firstInner
                          );
                        }}
                        className="w-full px-3 py-2 border rounded-md bg-white"
                      >
                        {columnOptions.map((col) => (
                          <option key={col.colId} value={col.field}>
                            {capitalizeEachWord(
                              String(col.headerName || col.field)
                            )}
                          </option>
                        ))}
                      </select>
                    </VStack>

                    <VStack className="flex-1">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Filter By
                      </label>
                      <select
                        value={filter.selectedInnerFilter || ""}
                        onChange={(e) => {
                          const selectedTitle = e.target.value;
                          const selectedInner =
                            columnMeta.innerFiltersMetadata.find(
                              (inner) => inner.title === selectedTitle
                            );
                          if (selectedInner) {
                            const allowed = operatorOptionsForKind(
                              selectedInner.kind
                            ) as readonly string[];
                            const nextOperator = allowed.includes(
                              filter.operator
                            )
                              ? filter.operator
                              : (defaultOperatorForKind(
                                  selectedInner.kind
                                ) as FilterRule["operator"]);
                            updateFilter(
                              filter.id,
                              "selectedInnerFilter",
                              selectedTitle
                            );
                            updateFilter(filter.id, "operator", nextOperator);
                          }
                        }}
                        disabled={!hasInnerFilters}
                        className="w-full px-3 py-2 border rounded-md bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        {hasInnerFilters ? (
                          columnMeta.innerFiltersMetadata.map((inner) => (
                            <option key={inner.title} value={inner.title}>
                              {capitalizeEachWord(String(inner.title))}
                            </option>
                          ))
                        ) : (
                          <option value="">N/A</option>
                        )}
                      </select>
                    </VStack>

                    <VStack className="flex-1">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Operator
                      </label>
                      <select
                        value={filter.operator}
                        onChange={(e) =>
                          updateFilter(filter.id, "operator", e.target.value)
                        }
                        className="w-full px-3 py-2 border rounded-md bg-white"
                      >
                        {ops.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === "inRange"
                              ? "In Range"
                              : opt === "notEqual"
                              ? "Not Equal"
                              : opt === "startsWith"
                              ? "Starts With"
                              : opt === "endsWith"
                              ? "Ends With"
                              : opt === "notContains"
                              ? "Not Contains"
                              : opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </option>
                        ))}
                      </select>
                    </VStack>

                    <VStack className="flex-1">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Delimiter
                      </label>
                      <select
                        value={filter.delimiter}
                        onChange={(e) =>
                          updateFilter(filter.id, "delimiter", e.target.value)
                        }
                        className="w-full px-3 py-2 border rounded-md bg-white"
                      >
                        <option value="auto">Auto-detect</option>
                        <option value="comma">Comma (,)</option>
                        <option value="space">Space</option>
                        <option value="newline">New Line</option>
                      </select>
                    </VStack>

                    <Icon
                      icon="mdi:delete"
                      className="cursor-pointer text-red-500 hover:text-red-700 mb-2"
                      width={24}
                      onClick={() => removeFilter(filter.id)}
                    />
                  </HStack>

                  <VStack>
                    <label className="text-sm font-medium text-gray-700 mb-1">
                      Values
                    </label>
                    <textarea
                      value={filter.value}
                      onChange={(e) =>
                        updateFilter(filter.id, "value", e.target.value)
                      }
                      placeholder={`Paste values from Excel or enter manually...\nExample:\nA\nB\nC`}
                      className="w-full px-3 py-2 border rounded-md min-h-[120px] font-mono text-sm bg-white"
                    />
                    <HStack className="justify-between mt-1">
                      <span className="text-xs text-gray-500">
                        {parseValues(filter.value, filter.delimiter).length}{" "}
                        value(s) detected
                      </span>
                      {filter.delimiter === "auto" && filter.value && (
                        <span className="text-xs text-blue-600">
                          Auto-detected:{" "}
                          {filter.value.includes("\n")
                            ? "New Line"
                            : filter.value.includes(",")
                            ? "Comma"
                            : /\s/.test(filter.value)
                            ? "Space"
                            : "Single Value"}
                        </span>
                      )}
                    </HStack>
                  </VStack>
                </VStack>
              );
            })}
          </VStack>
        </DialogContent>
        <DialogActions>
          <HStack className="gap-3 p-4 w-full justify-between">
            <ButtonSecondary onClick={() => setOpen(false)}>
              Cancel
            </ButtonSecondary>
            <HStack className="gap-3">
              <ButtonSecondary onClick={handleClear}>Clear All</ButtonSecondary>
              <ButtonPrimary
                onClick={handleApply}
                disabled={!columnOptions.length}
              >
                Apply Filters
              </ButtonPrimary>
            </HStack>
          </HStack>
        </DialogActions>
      </Dialog>
    </>
  );
}
